import { useQuery } from '@tanstack/react-query';
import type { Translated } from '@uefadigital/panenka';
import { PkButton, PkDialog, PkDivider, PkIcon, PkSpinner } from '@uefadigital/panenka-react';
import clsx from 'clsx';
import parseHtmlToReact from 'html-react-parser';
import { Fragment, memo } from 'react';

import { useWindowSize } from '@hooks/useWindowSize';
import { getEditorialByTagQuery } from '@queries/editorial';
import TeamIdentifier from '@shared/Identifier/TeamIdentifier';
import type { Team } from '@shared-types/match.types';
import { language } from '@utilities/browser';
import { stripHtml } from '@utilities/dom';
import { competitionCode } from '@utilities/WindowContext';

import * as styles from './styles.module.scss';

const Dialog = ({ selectedTeam, handleClose }: { selectedTeam: Translated<Team>; handleClose: () => unknown }) => {
  const { isMobile } = useWindowSize();
  const ARTICLE_TAG = `${competitionCode}.TICKETS.PNA.${selectedTeam?.teamCode}`.toUpperCase();
  const { data: article } = useQuery({
    ...getEditorialByTagQuery(ARTICLE_TAG, 'EN'),
    placeholderData: {},
    enabled: !!selectedTeam,
  });

  return (
    <PkDialog
      className={styles.dialog}
      isOpen={!!selectedTeam}
      onPkClose={handleClose}
      size={isMobile ? 'full' : 'large'}
    >
      <div slot='header' className='pk-w--100'>
        {selectedTeam && (
          <TeamIdentifier
            className={clsx(styles.identifier, 'pk-py--0')}
            team={selectedTeam}
            badgeSize={isMobile ? 48 : 54}
            customSecondaryText={language !== 'en' && selectedTeam.translations?.countryName?.['EN']}
            primarySize={24}
            secondarySize={16}
          />
        )}
        <PkDivider className='pk-my--m pk-my-sm--xl' />
      </div>

      <div slot='body' className='pk-w--100'>
        {article?.body ? (
          <div className='pk-d--flex pk-flex--column pk-align-items--start'>
            {article.body.map((part, i) => (
              <Fragment key={`part_${i}`}>
                {part._type === 'EXTRA' && (
                  <div
                    className={clsx(
                      'pk-d--flex',
                      'pk-flex--column',
                      'pk-flex-sm--row',
                      'pk-align-items--start',
                      'pk-gap--s',
                      i > 0 && 'pk-mt--l',
                    )}
                  >
                    <div
                      className={clsx(
                        'pk-d--flex',
                        'pk-align-items--center',
                        'pk-bg--ui-02',
                        'pk-px--xs',
                        'pk-py--xs2',
                        'pk-rounded--xl',
                        'pk-font-size--s',
                        'pk-text--text-05',
                        styles.chip,
                      )}
                    >
                      <span>{stripHtml(part._json.at(0)._html)}</span>
                    </div>
                    <div className={clsx('pk-text--text-01 pk-font-size--m pk-line-height--md', styles.body)}>
                      {parseHtmlToReact(part._json.at(-1)._html ?? '')}
                    </div>
                  </div>
                )}
                {part._type === 'LINK' && (
                  <PkButton
                    className='pk-mt--xl2'
                    appearance='text'
                    linkHref={part._attributes.href}
                    linkTarget={part._attributes.target}
                  >
                    {part._text}
                    <PkIcon name='navigation-chevron-right' color='interaction' />
                  </PkButton>
                )}
                {part._type === 'PARAGRAPH' && (
                  <div className='pk-mt--m pk-text--text-02 pk-font-size--s'>{stripHtml(part._html)}</div>
                )}
              </Fragment>
            ))}
          </div>
        ) : (
          <PkSpinner />
        )}
      </div>
    </PkDialog>
  );
};

export default memo(Dialog);
